/**
 * DataMapperNode - Map a value through a user-defined expression
 *
 * The expression uses `x` as the input variable.
 * Examples: "1/x", "x*2+1", "Math.sin(x)", "Math.pow(3, -x)"
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import type { Dependencies } from '@nodes/Dependencies';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import { internals, params } from '@/nodes/TypedEndpoints';

const endpoints = {
  ...params({x: {  },
  expression: { dataType: 'string' as const, value: 'x', label: 'f(x)' },

 out: { isOutput: true, label: 'Out', readonly: true },
  })
}

class DataMapperNode extends BaseNode<typeof endpoints> {
  private mapFn: (x: number, _p: any) => number = (x) => x;

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();
    this.compileExpression();
    this.evaluate();
  }

  onParamChanged(_payload: Payload, endpointId: string): void {
    if (endpointId !== 'x') {
      this.compileExpression();
    }
    this.evaluate();
  }

  private compileExpression(): void {
    const expr = this.params.expression.value;
    if (!expr || typeof expr !== 'string') {
      this.mapFn = (x) => x;
      return;
    }
    const customIds = Object.keys(this.deps.endpointApi.getCustomParams());
    const paramDecls = customIds.map(id => `var ${id} = _p.${id}.value;`).join(' ');
    try {
      this.mapFn = new Function('x', '_p', `${paramDecls} return (${expr})`) as (x: number, _p: any) => number;
    } catch (e) {
      this.logTrace(`invalid expression: ${expr}`);
      this.mapFn = (x) => x;
    }
  }

  private evaluate(): void {
    const x = this.params.x.value;
    try {
      const result = this.mapFn(x, this.params);
      if (typeof result === 'number' && isFinite(result)) {
        this.params.out.set(result);
      }
    } catch (e) {
      this.logTrace(`evaluation error for x=${x}`);
    }
  }
}

export const dataMapperNodeDef: NodeDef<DataMapperNode> = {
  type: 'DataMapper',
  shortName: 'map',
  domain: Domain.Data,
  info: 'Map a value through an expression.\nUse x as input variable.\nExamples: 1/x, x*2, Math.sin(x)',
  endpoints,
  allowAddParam: true,
  ctor: DataMapperNode
};
